// Per-tenant Yabbit SMS (YABBR) credential editor. Same shape as the
// GymSales TenantConfigsCard — one row per domain, expand → edit → test →
// save. Api-key is only sent when changed (blank preserves stored value).
// UI labels always say "Yabbit SMS" (product branding); internally the
// backend talks to api.yabbr.io.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Building2, Key, Save, Eye, EyeOff, Send, CheckCircle2, XCircle, MessageSquare,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const YabbitSmsAdminCard = () => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState({});
  const [showKey, setShowKey] = useState({});
  const [testResults, setTestResults] = useState({});

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await axios.get(`${API}/admin/yabbit-sms/tenant-configs`);
      setRows(r.data.domains || []);
    } catch (e) { setErr(e?.response?.data?.detail || 'Failed to load Yabbit SMS configs.'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    const c = row.creds?.tenant || {};
    setEdit((s) => ({
      ...s,
      [row.domain_id]: {
        enabled: !!c.enabled,
        from_number: c.from_number || '',
        api_key: '', // never prefill secret
      },
    }));
    setExpandedId(row.domain_id);
  };

  const save = async (row) => {
    const draft = edit[row.domain_id];
    if (!draft) return;
    setBusy((s) => ({ ...s, [row.domain_id]: 'save' }));
    try {
      await axios.patch(`${API}/admin/yabbit-sms/tenant-configs/${row.domain_id}`, draft);
      await load();
      setExpandedId(null);
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed.'); }
    finally { setBusy((s) => { const n = { ...s }; delete n[row.domain_id]; return n; }); }
  };

  const test = async (row) => {
    const draft = edit[row.domain_id] || {};
    setBusy((s) => ({ ...s, [row.domain_id]: 'test' }));
    setTestResults((s) => ({ ...s, [row.domain_id]: null }));
    try {
      const r = await axios.post(
        `${API}/admin/yabbit-sms/tenant-configs/${row.domain_id}/test`,
        { api_key: draft.api_key || undefined, from_number: draft.from_number || undefined },
      );
      setTestResults((s) => ({ ...s, [row.domain_id]: r.data }));
    } catch (e) {
      setTestResults((s) => ({ ...s, [row.domain_id]: { ok: false, error: e?.response?.data?.detail || 'Test failed.' } }));
    } finally { setBusy((s) => { const n = { ...s }; delete n[row.domain_id]; return n; }); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/50 overflow-hidden" data-testid="yabbit-sms-admin-card">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-purple-300" />
        <h3 className="text-sm font-bold text-white">Yabbit SMS (per-tenant credentials)</h3>
        <span className="ml-auto text-[10px] text-white/40">
          Env fallback: {process.env.REACT_APP_YABBIT_SMS_ENV_HINT || 'YABBR_API_KEY + YABBR_FROM'}
        </span>
      </div>
      {err && <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5 m-3">{err}</div>}
      {rows === null ? (
        <div className="text-white/40 text-xs italic p-4">
          <Loader2 className="w-3 h-3 animate-spin inline mr-1" />Loading Yabbit SMS configs…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-white/40 text-xs italic p-4">No domains configured.</div>
      ) : (
        <div className="divide-y divide-white/5">
          {rows.map((row) => {
            const c = row.creds || {};
            const source = c.source;
            const isExpanded = expandedId === row.domain_id;
            const draft = edit[row.domain_id] || {};
            const busyState = busy[row.domain_id];
            const testResult = testResults[row.domain_id];
            const tone = source === 'tenant' ? 'text-emerald-300' : source === 'env' ? 'text-amber-300' : 'text-rose-300';
            return (
              <div key={row.domain_id} className="p-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5 text-white/40" />
                  <span className="text-sm text-white font-semibold">{row.name}</span>
                  <span className={`text-[10px] font-mono ml-auto ${tone}`} data-testid={`yabbit-sms-source-${row.domain_id.slice(0, 8)}`}>
                    {source === 'tenant' ? '● tenant override' : source === 'env' ? '○ env fallback' : '⨯ no creds'}
                  </span>
                  <button
                    type="button"
                    onClick={() => isExpanded ? setExpandedId(null) : startEdit(row)}
                    className="text-[11px] font-semibold text-white bg-purple-600 hover:bg-purple-500 px-2 py-1 rounded"
                    data-testid={`yabbit-sms-edit-${row.domain_id.slice(0, 8)}`}
                  >
                    {isExpanded ? 'Cancel' : (source === 'tenant' ? 'Edit' : 'Configure')}
                  </button>
                </div>
                {c.tenant?.from_number && (
                  <div className="text-[11px] text-white/50 mt-1 pl-6">
                    From <span className="font-mono text-white/70">{c.tenant.from_number}</span>
                    {c.tenant?.api_key_set && <span className="ml-2 text-emerald-300">· api-key stored</span>}
                    {!c.tenant?.enabled && <span className="ml-2 text-amber-300">· disabled</span>}
                  </div>
                )}
                {isExpanded && (
                  <div className="mt-3 pl-6 space-y-2 border-l-2 border-purple-500/40 py-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <label className="text-[10px] text-white/60 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!draft.enabled}
                          onChange={(e) => setEdit((s) => ({ ...s, [row.domain_id]: { ...draft, enabled: e.target.checked } }))}
                          className="rounded"
                        />
                        Enabled
                      </label>
                      <label className="text-[10px] text-white/60">
                        Sending number (from)
                        <input
                          type="text"
                          value={draft.from_number || ''}
                          onChange={(e) => setEdit((s) => ({ ...s, [row.domain_id]: { ...draft, from_number: e.target.value } }))}
                          placeholder="61400000000"
                          className="mt-0.5 w-full text-xs bg-slate-800 border border-white/10 rounded px-2 py-1 text-white font-mono"
                        />
                      </label>
                    </div>
                    <label className="text-[10px] text-white/60 block">
                      API key <Key className="w-2.5 h-2.5 inline" />
                      <span className="text-white/40"> (blank = keep stored)</span>
                      <div className="relative mt-0.5">
                        <input
                          type={showKey[row.domain_id] ? 'text' : 'password'}
                          value={draft.api_key || ''}
                          onChange={(e) => setEdit((s) => ({ ...s, [row.domain_id]: { ...draft, api_key: e.target.value } }))}
                          className="w-full text-xs bg-slate-800 border border-white/10 rounded px-2 py-1 pr-8 text-white font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => setShowKey((s) => ({ ...s, [row.domain_id]: !s[row.domain_id] }))}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                        >
                          {showKey[row.domain_id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                      </div>
                    </label>
                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => test(row)}
                        disabled={busyState === 'test'}
                        className="text-[11px] font-semibold text-white bg-slate-600 hover:bg-slate-500 disabled:opacity-40 px-2 py-1 rounded inline-flex items-center gap-1"
                        data-testid={`yabbit-sms-test-${row.domain_id.slice(0, 8)}`}
                      >
                        {busyState === 'test' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Test (simulated)
                      </button>
                      <button
                        type="button"
                        onClick={() => save(row)}
                        disabled={busyState === 'save'}
                        className="text-[11px] font-semibold text-white bg-purple-600 hover:bg-purple-500 disabled:opacity-40 px-2 py-1 rounded inline-flex items-center gap-1"
                        data-testid={`yabbit-sms-save-${row.domain_id.slice(0, 8)}`}
                      >
                        {busyState === 'save' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Save
                      </button>
                    </div>
                    {testResult && (
                      <div
                        className={`text-[11px] rounded px-2 py-1 border ${
                          testResult.ok
                            ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40'
                            : 'bg-rose-500/10 text-rose-300 border-rose-500/40'
                        }`}
                        data-testid={`yabbit-sms-test-result-${row.domain_id.slice(0, 8)}`}
                      >
                        {testResult.ok ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <XCircle className="w-3 h-3 inline mr-1" />}
                        {testResult.ok
                          ? `Yabbit SMS reached OK · ${testResult.latency_ms}ms · source: ${testResult.source}`
                          : `Failed: ${testResult.error || testResult.status_code}`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default YabbitSmsAdminCard;
